import React, { useState } from 'react'
import { useConversations } from '../hooks/useConversations'
import ConversationListItem from './ConversationListItem'
import { Loader2, MessageSquare, Search } from 'lucide-react'
import { SortField } from '../types/conversation'
import './ConversationsList.css'

interface ConversationsListProps {
  onLoadChat: (phone: string) => void
}

const ConversationsList: React.FC<ConversationsListProps> = ({ onLoadChat }) => {
  const {
    conversations,
    loading,
    error,
    filters,
    sortConfig,
    availableTags,
    pagination,
    observerRef,
    applyFilters,
    applySort,
    getTagCount,
  } = useConversations()

  const [selectedTag, setSelectedTag] = useState<string>('all')
  const [searchTerm, setSearchTerm] = useState<string>('')

  const tagColorMap: { [key: string]: string } = {
    'interested': '#10B981',
    'thinking': '#F59E0B',
    'contracted': '#3B82F6',
    'lost': '#EF4444',
    'follow_up': '#8B5CF6',
    'no_tag': '#9CA3AF'
  }

  const formatTagLabel = (tag: string): string => {
    const labelMap: { [key: string]: string } = {
      'interested': 'Interesado',
      'thinking': 'Pensándoselo',
      'contracted': 'Contratado',
      'lost': 'Perdido',
      'follow_up': 'Seguimiento'
    }
    return labelMap[tag] || tag
  }

  const getTagColor = (tag: string) => {
    return tagColorMap[tag] || '#6B7280'
  }

  const getSortIndicator = (field: SortField) => {
    if (sortConfig.field !== field) {
      return <span className="sort-indicator none"></span>
    }
    return <span className={`sort-indicator ${sortConfig.direction}`}></span>
  }

  const handleTagChange = (tag: string) => {
    setSelectedTag(tag)
    applyFilters({ ...filters, tag: tag === 'all' ? undefined : tag })
  }

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setSearchTerm(value)
    applyFilters({ ...filters, search: value })
  }

  const handleSort = (field: SortField) => {
    applySort(field)
  }

  return (
    <div className="conversations-list">
      <div className="conversations-header">
        <h2>
          <MessageSquare size={20} />
          Conversaciones ({conversations.length})
        </h2>
      </div>

      <div className="conversations-controls">
        <div className="search-box">
          <Search size={16} />
          <input
            type="text"
            placeholder="Buscar por nombre o teléfono..."
            value={searchTerm}
            onChange={handleSearchChange}
          />
        </div>
      </div>

      <div className="tag-filters">
        <button
          className={`tag-filter ${selectedTag === 'all' ? 'active' : ''}`}
          style={{
            backgroundColor: selectedTag === 'all' ? '#6B7280' : 'transparent',
            borderColor: '#6B7280',
            color: selectedTag === 'all' ? 'white' : '#6B7280'
          }}
          onClick={() => handleTagChange('all')}
        >
          Todas ({getTagCount('all')})
        </button>
        {availableTags.map(tag => (
          <button
            key={tag}
            className={`tag-filter ${selectedTag === tag ? 'active' : ''}`}
            style={{
              backgroundColor: selectedTag === tag ? getTagColor(tag) : 'transparent',
              borderColor: getTagColor(tag),
              color: selectedTag === tag ? 'white' : getTagColor(tag)
            }}
            onClick={() => handleTagChange(tag)}
          >
            {formatTagLabel(tag)} ({getTagCount(tag)})
          </button>
        ))}
      </div>

      {loading && conversations.length === 0 ? (
        <div className="initial-loading">
          <Loader2 className="animate-spin" size={32} />
          <span>Cargando conversaciones...</span>
        </div>
      ) : error ? (
        <div className="error">{error}</div>
      ) : (
        <div className="conversations-container">
          <div className="conversations-table">
            <div className="table-header">
              <div 
                className="table-header-cell sortable"
                onClick={() => handleSort('name')}
              >
                Cliente {getSortIndicator('name')}
              </div>
              <div 
                className="table-header-cell sortable"
                onClick={() => handleSort('messageCount')}
              >
                Mensajes {getSortIndicator('messageCount')}
              </div>
              <div 
                className="table-header-cell sortable"
                onClick={() => handleSort('toolsUsed')}
              >
                Tools {getSortIndicator('toolsUsed')}
              </div>
              <div 
                className="table-header-cell sortable"
                onClick={() => handleSort('tripsConsulted')}
              >
                Viajes {getSortIndicator('tripsConsulted')}
              </div>
              <div 
                className="table-header-cell sortable"
                onClick={() => handleSort('lastMessageAt')}
              >
                Último {getSortIndicator('lastMessageAt')}
              </div>
              <div className="table-header-cell">
                Tag
              </div>
              <div className="table-header-cell">
                Chat
              </div>
            </div>
            
            {conversations.length === 0 ? (
              <div className="no-results">
                No hay conversaciones que coincidan con los filtros seleccionados
              </div>
            ) : (
              <>
                {conversations.map(conversation => (
                  <ConversationListItem
                    key={conversation.whatsappPhone}
                    conversation={conversation}
                    onLoadChat={onLoadChat}
                  />
                ))}
                
                {pagination.hasMore && (
                  <div ref={observerRef} className="load-more-trigger">
                    {pagination.loading && (
                      <div className="loading-more">
                        <Loader2 className="animate-spin" size={20} />
                        <span>Cargando más conversaciones...</span>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default ConversationsList
